import { useCallback } from 'react';

import { type CustomClientFormType } from '@ubnt/wifi-calculator/device/customClients';

import { useDispatch, useSelector } from 'app/store';
import { trackEvent } from 'utils/googleAnalytics';
import CustomClientForm, { CustomClientFormProps } from '../components/CustomClientForm/CustomClientForm';
import {
  devicesCreateCustomClientDevice,
  devicesCustomClientDeviceInstanceMapSelector,
  devicesCustomClientFormEditDeviceSelector,
  devicesCustomClientFormOpenChange,
  devicesCustomClientFormOpenSelector,
  devicesRemoveCustomClientDevice,
  devicesUsedNamesSelector,
} from '../slice';
import { devicesUpdateCustomClientDevice } from '../externalActions';

const CustomClientFormContainer = (props: Partial<CustomClientFormProps>) => {
  const open = useSelector(devicesCustomClientFormOpenSelector);
  const usedNames = useSelector(devicesUsedNamesSelector);
  const externalDeviceMap = useSelector(devicesCustomClientDeviceInstanceMapSelector);
  const editDeviceName = useSelector(devicesCustomClientFormEditDeviceSelector);

  const dispatch = useDispatch();

  const onClose = useCallback(() => {
    dispatch(devicesCustomClientFormOpenChange(false));
  }, [dispatch]);

  const onRemoveClick = useCallback(() => {
    if (editDeviceName) {
      dispatch(devicesRemoveCustomClientDevice(editDeviceName));
      dispatch(devicesCustomClientFormOpenChange(false));
      trackEvent('clientFormAction', { action: 'remove' });
    }
  }, [editDeviceName, dispatch]);

  const onSubmit = useCallback(
    (formData: Required<CustomClientFormType>) => {
      if (editDeviceName) {
        dispatch(devicesUpdateCustomClientDevice(formData));
        trackEvent('clientFormAction', { action: 'edit' });
      } else {
        dispatch(devicesCreateCustomClientDevice(formData));
        trackEvent('clientFormAction', { action: 'create' });
      }
    },
    [dispatch, editDeviceName]
  );

  return (
    <CustomClientForm
      editDeviceName={editDeviceName}
      externalDeviceMap={externalDeviceMap}
      open={open}
      onClose={onClose}
      onRemoveClick={onRemoveClick}
      onSubmit={onSubmit}
      usedNames={usedNames}
      {...props}
    />
  );
};

export default CustomClientFormContainer;
